import { useState } from "react";
import { logger } from "../lib/logger";
import { X, Loader2, Wand2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";

const PRESETS = [
  { l: "Shorter", v: "Make it shorter and more concise" },
  { l: "More Persuasive", v: "Make it more persuasive with stronger calls to action" },
  { l: "Add Humor", v: "Add subtle humor while keeping it professional" },
  { l: "More Detail", v: "Add more specific details and examples" },
  { l: "Simpler", v: "Simplify for a broader audience" },
  { l: "More Formal", v: "Make the tone more formal and authoritative" },
];

export function RefineModal({ genId, onClose, onRefined }) {
  const [inst, setInst] = useState("");
  const [loading, setLoading] = useState(false);

  const go = async () => {
    if (!inst.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/refine", { generation_id: genId, instruction: inst });
      toast.success("Refined! 1 credit used.");
      onRefined(data);
      onClose();
    } catch (err) {
      logger.error("Refine error", err);
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="refine-modal">
      <div className="bg-white border border-zinc-200 w-full max-w-lg shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-brand" />
            <h3 className="font-bold text-zinc-900 font-heading">Refine with AI</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-2">Quick presets</label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.l}
                  onClick={() => setInst(p.v)}
                  data-testid={`preset-${p.l.toLowerCase().replace(/ /g, "-")}`}
                  className={`text-xs font-semibold px-3 py-1.5 border transition-all ${inst === p.v ? "border-brand bg-brand/5 text-brand" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-1">Or describe your refinement</label>
            <textarea
              value={inst}
              onChange={(e) => setInst(e.target.value)}
              rows={3}
              placeholder="e.g., Make it more emotional..."
              className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none resize-none text-sm"
              data-testid="refine-instruction-input"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400 flex items-center gap-1"><Zap className="w-3 h-3" /> 1 credit</span>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-600">Cancel</button>
              <button onClick={go} disabled={loading || !inst.trim()} data-testid="refine-submit-btn" className="bg-brand text-white font-semibold px-5 py-2 text-sm hover:bg-brand-hover flex items-center gap-2 disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {loading ? "Refining..." : "Refine"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
